import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { PARTICIPANT_DOCUMENTS_QUERY_KEY } from '../../utils/invalidate_signed_document_queries';
const PARTICIPANT_DOCUMENTS_PAGE_SIZE = 100;
const PARTICIPANT_DOCUMENTS_MAX_PAGES = 20;
const fetchParticipantDocuments = async (subject, apiClient, accountId, siteId) => {
    if (!subject.id) {
        return [];
    }
    // The subject's ec__documents list path ignores where, sort, limit and skip and always returns
    // the oldest 100, so newer documents never reach the table. ec__site_documents honours all four.
    const documents = [];
    let skip = 0;
    for (let page = 0; page < PARTICIPANT_DOCUMENTS_MAX_PAGES; page += 1) {
        const response = await apiClient.get(`/v2/account/${accountId}/ec__site_documents`, {
            params: {
                where: JSON.stringify({ ec__site: siteId, ec__primary_participant: subject.id }),
                paths: [
                    'ec__document_template.ec__title',
                    'ec__status',
                    'ec__required_signers',
                    'created',
                    'ec__pts',
                    'ec__pts_signature_date_wallclock',
                    'ec__final_document',
                ],
                sort: JSON.stringify({ created: -1 }),
                limit: PARTICIPANT_DOCUMENTS_PAGE_SIZE,
                skip,
                passive: true,
            },
        });
        const pageDocuments = response?.data?.data ?? [];
        documents.push(...pageDocuments);
        const hasMore = response?.data?.hasMore === true && pageDocuments.length === PARTICIPANT_DOCUMENTS_PAGE_SIZE;
        if (!hasMore) {
            break;
        }
        skip += PARTICIPANT_DOCUMENTS_PAGE_SIZE;
    }
    return documents.map((document) => {
        if (!subject.displayName) {
            return document;
        }
        return {
            ...document,
            ec__document_template: {
                ...document.ec__document_template,
                ec__title: `${subject.displayName} • ${document.ec__document_template.ec__title}`,
            },
        };
    });
};
export const useParticipantDocuments = ({ participant, accountId, siteId, apiClient, isCaregiverEnabled = false, formatCaregiverDisplayName = (name) => name, }) => {
    const getParticipantDocumentsData = useCallback(async () => {
        if (!participant?._id || !accountId || !siteId) {
            return [];
        }
        try {
            const subjects = [
                { id: participant._id, displayName: undefined },
                ...(isCaregiverEnabled ? participant.c_caregiver_relationship?.c_caregivers_info || [] : []).map(({ c_public_user, c_caregiver_display_name }) => ({
                    id: c_public_user?._id,
                    displayName: formatCaregiverDisplayName(c_caregiver_display_name),
                })),
            ].filter(({ id }) => !!id);
            const documentsPromises = subjects.map((subject) => fetchParticipantDocuments(subject, apiClient, accountId, siteId));
            const results = await Promise.allSettled(documentsPromises);
            return results
                .filter((result) => result.status === 'fulfilled')
                .flatMap((result) => result.value);
        }
        catch {
            return [];
        }
    }, [accountId, apiClient, formatCaregiverDisplayName, isCaregiverEnabled, participant, siteId]);
    return useQuery({
        queryKey: [PARTICIPANT_DOCUMENTS_QUERY_KEY, participant?._id],
        queryFn: getParticipantDocumentsData,
        enabled: !!participant?._id && !!accountId && !!siteId,
    });
};
